import { useState, useMemo } from 'react';
import { ArrowUpDown } from 'lucide-react';

interface DataTableViewProps {
  data: Record<string, unknown>[];
}

/**
 * Fallback sortable data table for ERAX query results.
 */
function isNumericValue(val: unknown): boolean {
  if (typeof val === 'number') return isFinite(val);
  if (typeof val === 'string') {
    const cleaned = String(val).replace(/[,\s%$]/g, '').trim();
    return cleaned !== '' && !Number.isNaN(Number(cleaned));
  }
  return false;
}

export function DataTableView({ data }: DataTableViewProps) {
  const columns = useMemo(() => (data.length > 0 ? Object.keys(data[0]) : []), [data]);
  const columnIsNumeric = useMemo(() => {
    const result: Record<string, boolean> = {};
    columns.forEach((col) => {
      const values = data.map((row) => row[col]);
      result[col] = values.length > 0 && values.every((v) => isNumericValue(v));
    });
    return result;
  }, [data, columns]);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = useMemo(() => {
    if (!sortCol) return data;
    return [...data].sort((a, b) => {
      const va = a[sortCol];
      const vb = b[sortCol];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortAsc ? va - vb : vb - va;
      }
      const sa = String(va ?? '');
      const sb = String(vb ?? '');
      return sortAsc ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [data, sortCol, sortAsc]);

  const toggleSort = (col: string) => {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(true);
    }
  };

  if (data.length === 0) return <p className="text-xs text-gray-500">No data available.</p>;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {columns.map((col) => (
              <th
                key={col}
                onClick={() => toggleSort(col)}
                className={`px-2 py-1.5 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap ${columnIsNumeric[col] ? 'text-right' : 'text-left'}`}
              >
                <span className="inline-flex items-center gap-0.5">
                  {col}
                  <ArrowUpDown size={9} className={sortCol === col ? 'text-indigo-600' : 'text-gray-400'} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
            {sorted.map((row, idx) => (
            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
              {columns.map((col) => {
                const val = row[col];
                const isNum = columnIsNumeric[col];
                const display = isNum
                  ? (typeof val === 'number' ? val : Number(String(val).replace(/[,\s%$]/g, '')))
                  : null;
                return (
                  <td
                    key={col}
                    className={`px-2 py-1 whitespace-nowrap ${isNum ? 'text-right tabular-nums' : 'text-left'}`}
                  >
                    {isNum && display !== null && !Number.isNaN(display)
                      ? display.toLocaleString()
                      : String(val ?? '')}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
